import {
  MutationCache,
  QueryClient,
  type DefaultError,
  type Mutation,
} from '@tanstack/react-query';
import i18n from 'i18next';
import toast from 'react-hot-toast/headless';

/**
 * Global fallback for mutation errors. Historically a mutation without its own
 * `onError` failed *silently* — the server error was swallowed with no user
 * feedback. This surfaces a generic error toast for any such mutation, while
 * deferring to a mutation that defines its own `onError` so we never clobber
 * (or double up on) existing per-mutation handling.
 */
export const onMutationErrorFallback = (
  _error: DefaultError,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown>,
): void => {
  // Respect any existing per-mutation handler — don't clobber it.
  if (mutation.options.onError) return;
  toast.error(i18n.t('error.generic'));
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 60000,
    },
  },
  mutationCache: new MutationCache({
    onError: onMutationErrorFallback,
  }),
});
